<template>
  <div id="radar-grid-layer"></div>
</template>

<script>
import Vue from "vue";
import { RadarGridMixin } from "@/mixins";
import AuthService from "@/services/authService";
import RadarGridService from "./services/radarGridService";
import { createNamespacedHelpers } from "vuex";
import { RGGeoUtil } from "./utils/";
import { RADAR_GRID_IDS } from "@/views/enterprise/live-map/layers/radar-grid/consts/radarGridLayerConstants";
import { useMapbox } from "@/views/enterprise/live-map/layers/radar-grid/composables/useMapbox";
import { usePopupManager } from "@/views/enterprise/live-map/layers/radar-grid/composables/usePopupManager";
import { usePathManager } from "@/views/enterprise/live-map/layers/radar-grid/composables/usePathManager";
import { useRadarGridEvents } from "@/views/enterprise/live-map/layers/radar-grid/composables/useRadarGridEvents";

const { mapState: mapStateOfLiveMap } = createNamespacedHelpers("liveMap");

export default {
  name: "RadarGridLayer",
  mixins: [RadarGridMixin],
  data() {
    return {
      initialized: false,
      intervalId: null,
      rgBasesObject: null,
      radarBaseGroupId: null,
      latestRadarGridObjects: {},
      mapbox: null,
      pathManager: null,
      popupManager: null,
    };
  },

  props: {
    map: Object,
    isReady: { type: Boolean, default: false },
    mapEvent: String,
  },

  watch: {
    async isReady() {
      if (this.isReady) {
        await this.addLayersAndSources();
        if (!this.initialized) {
          await this.addClickEvent();
          await RadarGridService.setExternalToken();
          this.initialized = true;
        }
        this.radarBaseGroupId = await RadarGridService.fetchRadarBaseGroupId();
      }
    },

    radarBaseGroupId() {
      if (this.radarBaseGroupId) {
        this.startFetchRadarGridObjects();
      }
    },

    latestRadarGridObjects() {
      if (Object.keys(this.latestRadarGridObjects).length !== 0) {
        let geojson = RGGeoUtil.createRadarGridGeoJson(
          this.latestRadarGridObjects
        );
        this.mapbox.setSourceData(RADAR_GRID_IDS.RADAR_SOURCE_ID, geojson);

        // popup/path 更新する
        this.updatePopupAndPath();
      }
    },

    filterRadarGrid: {
      handler() {
        if (this.isReady) {
          const visibility = this.filterRadarGrid.includes("radar_grid")
            ? "visible"
            : "none";

          this.mapbox.setLayerVisibility(
            RADAR_GRID_IDS.RADAR_LAYER_ID,
            visibility
          );

          this.mapbox.setLayerVisibility(
            RADAR_GRID_IDS.FRAME_LAYER_ID,
            visibility
          );
        }
      },
      deep: true,
    },
  },

  computed: {
    ...mapStateOfLiveMap({
      filterRadarGrid: state => state.filterRadarGrid,
    }),
  },

  methods: {
    async addLayersAndSources() {
      this.mapbox = useMapbox(this.map);
      await this.mapbox.addSources();
      await this.mapbox.addLayers();
    },

    async addClickEvent() {
      this.pathManager = usePathManager({
        map: this.map,
      });

      this.popupManager = usePopupManager({
        map: this.map,
        pathManager: this.pathManager,
      });

      const { setupRadarObjectClick } = useRadarGridEvents({
        map: this.map,
        popupManager: this.popupManager,
        pathManager: this.pathManager,
      });

      setupRadarObjectClick();
    },

    async fetchRadarGridObjects() {
      try {
        this.latestRadarGridObjects = await RadarGridService.fetchRadarGridObjects(
          this.radarBaseGroupId
        );
      } catch (e) {
        Vue.$log.error("RGオブジェクトの取得に失敗しました:", e);
        this.$vs.notify({
          title: "RGオブジェクトの取得に失敗しました",
          color: "danger",
          iconPack: "feather",
          icon: "icon-x-square",
          fixed: true,
        });
      }
    },

    startFetchRadarGridObjects() {
      // 初回即時取得
      this.fetchRadarGridObjects();

      // 10秒ごとの取得
      this.intervalId = setInterval(() => {
        this.fetchRadarGridObjects();
      }, 10000);
    },

    stopFetchRadarGridObjects() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = null;
      }
    },

    updatePopupAndPath() {
      if (!this.popupManager || !this.pathManager) return;

      const selectedIds = this.popupManager.getSelectedObjectIds();
      if (!selectedIds || selectedIds.length === 0) return;

      selectedIds.forEach(objectId => {
        const latestObject = this.latestRadarGridObjects.find(
          obj => obj.id === objectId
        );
        if (latestObject) {
          const normalizedProps = RGGeoUtil.normalizeRadarObjectProperties(
            latestObject
          );
          this.popupManager.updatePopup(normalizedProps);

          this.pathManager.addNewPointToPath(objectId, latestObject);
        }
      });
    },
  },

  beforeDestroy() {
    this.stopFetchRadarGridObjects();
  },
};
</script>

<style lang="scss">
#radar-grid-layer {
  width: 100%;
}
.is-focusing-radar-grid-object {
  pointer-events: none;
}
.rg-popup-wrapper {
  z-index: 11;
  .mapboxgl-popup-tip {
    display: none;
  }
  .mapboxgl-popup-content {
    display: contents;
  }
}
</style>
